import { apiClient } from 'axios-client';
import { format } from 'date-fns';
import {
  ImportedSubscriptionResponse,
  importSubscriptionsFromZoho,
  ZohoViewRequest,
} from 'generated/api-client';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Button, Card, Icon } from 'semantic-ui-react';
import { ToastError } from 'utils';

// Exported by the zoho stack (GET /zoho/subscription/export)
interface SubscriptionExport {
  status: 'running' | 'done' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  error: string | null;
  organizations: { done: number; total: number };
  recurringInvoices: { done: number; total: number };
  views: ZohoViewRequest[];
  unlinked: {
    zohoOrganizationId: string;
    recurringInvoiceId: string;
    name: string;
    customerName: string;
    total: number;
  }[];
}

const BATCH_SIZE = 200;
const POLLING_INTERVAL = 5_000;

const csvCell = (value: string | number | null): string =>
  `"${String(value ?? '').replace(/"/g, '""')}"`;

const downloadReport = (
  results: ImportedSubscriptionResponse[],
  subscriptionExport: SubscriptionExport,
): void => {
  const rows = [
    ['Fiche', 'Résultat', 'Raison'],
    ...results.map((result) => [
      result.viewId,
      result.imported ? 'Importé' : 'Non importé',
      result.reason,
    ]),
    ...subscriptionExport.unlinked.map((invoice) => [
      '',
      'Facture récurrente sans fiche',
      `${invoice.name} (${invoice.customerName}, ${invoice.total} €, organisation ${invoice.zohoOrganizationId}, id ${invoice.recurringInvoiceId})`,
    ]),
  ];

  const element = document.createElement('a');
  element.href = URL.createObjectURL(
    new Blob([rows.map((row) => row.map(csvCell).join(',')).join('\n')], {
      type: 'text/csv;charset=utf-8',
    }),
  );
  element.download = `reprise-abonnements-zoho-${format(
    new Date(),
    'yyyy-MM-dd',
  )}.csv`;
  document.body.appendChild(element);
  element.click();
  element.remove();
};

/**
 * Temporary, for the migration of the subscriptions to the backend:
 * 1. the zoho stack exports the recurring invoices (an hour or more)
 * 2. they are sent to the backend in batches, which rebuilds the subscriptions
 */
export const ImportZohoSubscriptionsCard: FunctionComponent = () => {
  const [subscriptionExport, setSubscriptionExport] =
    useState<SubscriptionExport | null>(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<ImportedSubscriptionResponse[]>([]);

  const fetchExport = useCallback(async (): Promise<void> => {
    const { data } = await apiClient.get<SubscriptionExport | null>(
      '/zoho/subscription/export',
    );
    setSubscriptionExport(data || null);
  }, []);

  useEffect(() => {
    fetchExport().catch(() =>
      ToastError('Erreur', "Impossible de récupérer l'export Zoho"),
    );
  }, [fetchExport]);

  const running = subscriptionExport?.status === 'running';

  useEffect(() => {
    if (!running) return;
    const interval = setInterval(() => {
      fetchExport().catch(() => undefined);
    }, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [running, fetchExport]);

  const onClickExport = useCallback(async (): Promise<void> => {
    setResults([]);
    await apiClient
      .post<SubscriptionExport>('/zoho/subscription/export')
      .then(({ data }) => setSubscriptionExport(data))
      .catch(() => ToastError('Erreur', "Impossible de lancer l'export Zoho"));
  }, []);

  const onClickImport = useCallback(async (): Promise<void> => {
    if (!subscriptionExport) return;

    setImporting(true);
    const imported: ImportedSubscriptionResponse[] = [];

    try {
      for (
        let index = 0;
        index < subscriptionExport.views.length;
        index += BATCH_SIZE
      ) {
        const { data } = await importSubscriptionsFromZoho({
          body: {
            views: subscriptionExport.views.slice(index, index + BATCH_SIZE),
          },
          throwOnError: true,
        });
        imported.push(...data.results);
        setResults([...imported]);
      }
    } catch {
      ToastError('Erreur', "L'import des abonnements a échoué");
    } finally {
      setImporting(false);
    }
  }, [subscriptionExport]);

  const importedCount = results.filter((result) => result.imported).length;

  return (
    <Card>
      <Card.Content>
        <Icon size="large" style={{ float: 'right' }} name="sync" />
        <Card.Header>Reprise des abonnements Zoho</Card.Header>
        <Card.Description>
          <p>
            Crée les abonnements du backend à partir des factures récurrentes
            actives de Zoho, qui fait foi : les abonnements existants sont
            remplacés. L&apos;export Zoho prend plus d&apos;une heure, cette
            page peut être fermée en attendant.
          </p>
          {subscriptionExport && running ? (
            <p>
              Export en cours
              {subscriptionExport.recurringInvoices.total
                ? ` : ${subscriptionExport.recurringInvoices.done}/${subscriptionExport.recurringInvoices.total} factures récurrentes (organisation ${Math.min(subscriptionExport.organizations.done + 1, subscriptionExport.organizations.total)}/${subscriptionExport.organizations.total})`
                : '…'}
            </p>
          ) : null}
          {subscriptionExport?.status === 'failed' ? (
            <p>Export en échec : {subscriptionExport.error}</p>
          ) : null}
          {subscriptionExport?.status === 'done' ? (
            <p>
              Export du{' '}
              {format(
                new Date(
                  subscriptionExport.finishedAt ?? subscriptionExport.startedAt,
                ),
                'dd/MM/yyyy à HH:mm',
              )}{' '}
              : {subscriptionExport.views.length} fiches,{' '}
              {subscriptionExport.unlinked.length} factures récurrentes sans
              fiche.
            </p>
          ) : null}
          {results.length ? (
            <p>
              {importing ? 'Import en cours : ' : 'Import terminé : '}
              {importedCount} importées, {results.length - importedCount} non
              importées.
            </p>
          ) : null}
        </Card.Description>
      </Card.Content>
      <Card.Content extra>
        <Button.Group vertical fluid>
          <Button
            primary
            loading={running}
            disabled={running || importing}
            onClick={onClickExport}
          >
            Lancer l&apos;export Zoho
          </Button>
          <Button
            primary
            loading={importing}
            disabled={subscriptionExport?.status !== 'done' || importing}
            onClick={onClickImport}
          >
            Importer dans le backend
          </Button>
          <Button
            disabled={!results.length || importing || !subscriptionExport}
            onClick={() =>
              subscriptionExport && downloadReport(results, subscriptionExport)
            }
          >
            Télécharger le rapport
          </Button>
        </Button.Group>
      </Card.Content>
    </Card>
  );
};
